import { PublicCalendarEventSerializer } from "@app-contexts/public/features/org-profile/data/types/public-venue";
import { PaginatorContract } from "@shared/services/api/types";
import PublicBasedVenuesRepository from "@shared/modules/venue/repositories/PublicBasedVenuesRepository";
import { CalendarCache, CacheParams } from "./CalendarCache";

export interface CalendarCacheWrapper {
  cache: CalendarCache;
  timestamp: number;
}

export interface CalendarQueryData {
  cache: CalendarCache;
  lastFetchedData?: PaginatorContract<PublicCalendarEventSerializer>;
}

export interface CalendarServiceResult {
  queryData: CalendarQueryData;
  cacheWrapper: CalendarCacheWrapper;
}

/**
 * CalendarEventService - Smart Caching Service for Calendar Events
 *
 * This service implements an intelligent caching layer for calendar events that works
 * in conjunction with React Query to provide optimal performance and user experience.
 *
 * Key Features:
 * - Interval-based caching: Tracks which date ranges have been fetched
 * - Smart cache validation: Prevents redundant API calls for overlapping date ranges
 * - Stale cache detection: Automatically refreshes cache when data becomes stale
 * - Cache merging: Combines new API responses with existing cached data
 * - Memory efficient: Only stores events within cached intervals
 *
 * Architecture:
 * This service acts as a bridge between React Query's caching mechanism and our
 * custom CalendarCache implementation. While React Query handles query state
 * management, this service provides domain-specific caching logic for calendar
 * events that understands date ranges and interval overlaps.
 */

export class CalendarEventService {
  private repository: PublicBasedVenuesRepository;
  private staleTime: number;

  constructor(repository: PublicBasedVenuesRepository, staleTime: number) {
    this.repository = repository;
    this.staleTime = staleTime;
  }

  /**
   * Processes query result data to extract the appropriate event data
   * based on cache state and date range requirements.
   *
   * @param queryResult - The raw query data from CalendarEventService
   * @param startDate - Query start date
   * @param endDate - Query end date (optional for unbounded queries)
   * @returns Processed event data or undefined if no data available
   */
  static processEventData(
    queryResult: CalendarQueryData | undefined,
    startDate: Date,
    endDate: Date | undefined
  ): PaginatorContract<PublicCalendarEventSerializer> | undefined {
    if (!queryResult?.lastFetchedData) {
      return undefined;
    }

    // If we have cached data, check if we need to filter it for the current range
    const { cache } = queryResult;
    if (cache && cache.isRangeFullyCached(startDate, endDate)) {
      const filteredEvents = cache.queryEventsInRange(startDate, endDate);
      return {
        ...queryResult.lastFetchedData,
        results: filteredEvents,
        count: filteredEvents.length,
      };
    }

    return queryResult.lastFetchedData;
  }

  async fetchEvents(
    params: CacheParams,
    startDate: Date,
    endDate: Date | undefined,
    existingCacheData: CalendarCacheWrapper | undefined,
    onSuccess?: (data: PaginatorContract<PublicCalendarEventSerializer>) => void
  ): Promise<CalendarServiceResult> {
    // Check if cache exists and is not stale
    const isStale = existingCacheData
      ? Date.now() - existingCacheData.timestamp > this.staleTime
      : false;

    if (isStale && existingCacheData) {
      // eslint-disable-next-line no-console
      console.debug(
        `⏰ CACHE STALE: Cache is ${Math.round(
          (Date.now() - existingCacheData.timestamp) / 1000
        )}s old (staleTime: ${this.staleTime / 1000}s), creating new cache`
      );
    }

    // Use existing cache if not stale, otherwise create new
    const cache =
      !existingCacheData || isStale
        ? new CalendarCache()
        : existingCacheData.cache;

    // Check if the requested range is fully cached
    const isRangeFullyCached = cache.isRangeFullyCached(startDate, endDate);

    if (isRangeFullyCached) {
      // Return cached events without making API call
      const cachedEvents = cache.queryEventsInRange(startDate, endDate);
      // eslint-disable-next-line no-console
      console.debug(
        `            🎯 CACHE HIT: Returning ${cachedEvents.length} cached events`
      );

      const cachedData = {
        results: cachedEvents,
        count: cachedEvents.length,
        num_pages: null,
        page_number: null,
      } as unknown as PaginatorContract<PublicCalendarEventSerializer>;

      // Call onSuccess for cache hits as well
      onSuccess?.(cachedData);

      return {
        queryData: {
          cache,
          lastFetchedData: cachedData,
        },
        cacheWrapper: {
          cache,
          timestamp: existingCacheData?.timestamp || Date.now(),
        },
      };
    }

    // Cache miss - need to fetch from API
    // eslint-disable-next-line no-console
    console.debug(`            📡 CACHE MISS: Fetching from API`);

    if (!params.slug) {
      throw new Error("Venue slug is required");
    }

    // Make API call for new dates
    const apiData = await this.repository.calendar({
      ...params,
      version: 2,
      slug: params.slug,
      ends_on__gte: startDate,
      starts_on__lt: endDate,
      purchase_source_platform: params.purchase_source_platform,
    } as Parameters<PublicBasedVenuesRepository["calendar"]>[0]);

    // Update the cache with new data
    const effectiveEnd = endDate ?? startDate; // Use exact same timestamp for unbounded queries
    cache.addEventsBatch(apiData.results, {
      start: startDate,
      end: effectiveEnd,
    });

    // Call onSuccess if provided
    onSuccess?.(apiData);

    return {
      queryData: {
        cache,
        lastFetchedData: apiData,
      },
      cacheWrapper: {
        cache,
        timestamp: Date.now(),
      },
    };
  }
}
